import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useI18n } from "@/contexts/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { ArrowRight, Network, Sparkles, ShieldCheck, Layers } from "lucide-react";
import { API } from "@/lib/api";
import TotalKlickLogo from "@/components/TotalKlickLogo";

export default function LandingPage() {
  const { t, lang } = useI18n();
  const [networks, setNetworks] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/public/networks`).then((r) => setNetworks(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white text-[#0A0A0A]" data-testid="landing-page">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
            <TotalKlickLogo size={36} />
            <div className="leading-tight">
              <div className="font-heading font-bold text-base">TotalKlick</div>
              <div className="overline text-muted-foreground">Hub</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <button
              data-testid="landing-login-btn"
              onClick={() => navigate("/login")}
              className="text-sm px-4 py-2 rounded-sm border border-foreground text-foreground hover:bg-foreground hover:text-white transition-colors"
            >
              {t("nav_login")}
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-32 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="overline text-brand mb-5" data-testid="landing-eyebrow">{t("landing_eyebrow")}</div>
            <h1 className="editorial-h1 text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] mb-7" data-testid="landing-h1">
              {t("landing_h1")}
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed mb-9" data-testid="landing-lead">
              {t("landing_lead")}
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                data-testid="landing-cta-demo"
                onClick={() => document.getElementById("networks-section")?.scrollIntoView({ behavior: "smooth" })}
                className="inline-flex items-center gap-2 px-6 h-12 rounded-sm bg-brand text-white text-sm font-medium hover:bg-brand-hover transition-colors"
              >
                {t("landing_cta_demo")} <ArrowRight className="w-4 h-4" />
              </button>
              <button
                data-testid="landing-cta-login"
                onClick={() => navigate("/login")}
                className="inline-flex items-center gap-2 px-6 h-12 rounded-sm border border-foreground text-sm font-medium hover:bg-foreground hover:text-white transition-colors"
              >
                {t("landing_cta_login")}
              </button>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] relative overflow-hidden rounded-sm border border-border">
              <img
                src="https://images.pexels.com/photos/5218431/pexels-photo-5218431.jpeg"
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand/30 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 bg-white/90 backdrop-blur-md p-5 rounded-sm">
                <div className="overline text-brand mb-1">Multi-network</div>
                <div className="font-heading font-bold text-xl">2 reti attive · 4 tenant · 7 prodotti</div>
                <div className="text-xs text-muted-foreground mt-1.5">Whitelabel infrastructure pronta in produzione</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <h2 className="font-heading font-bold text-3xl lg:text-5xl mb-12 max-w-3xl tracking-tight">
            {t("landing_features_title")}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-border">
            {[
              { icon: Network, k: "feature_multi_network", d: "feature_multi_network_desc" },
              { icon: Layers, k: "feature_vertical", d: "feature_vertical_desc" },
              { icon: ShieldCheck, k: "feature_sso", d: "feature_sso_desc" },
              { icon: Sparkles, k: "feature_storefront", d: "feature_storefront_desc" },
            ].map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="bg-white p-8 hover:bg-brand/[0.03] transition-colors" data-testid={`feature-${f.k}`}>
                  <Icon className="w-7 h-7 text-brand mb-5" strokeWidth={1.5} />
                  <h3 className="font-heading font-bold text-lg mb-2">{t(f.k)}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{t(f.d)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Networks showcase */}
      <section id="networks-section" className="border-b border-border bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <div className="mb-12">
            <div className="overline text-brand mb-3">Live demo</div>
            <h2 className="font-heading font-bold text-3xl lg:text-5xl tracking-tight mb-4" data-testid="networks-title">
              {t("landing_networks_title")}
            </h2>
            <p className="text-muted-foreground max-w-2xl">{t("landing_networks_lead")}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {networks.map((n, idx) => (
              <Link
                key={n.id}
                to={n.sample_rivenditore_slug ? `/s/${n.sample_rivenditore_slug}` : "#"}
                data-testid={`network-card-${n.network_key}`}
                className="group block bg-white border border-border rounded-sm overflow-hidden hover:border-brand hover:-translate-y-1 transition-all duration-300"
              >
                <div className="aspect-[16/9] relative overflow-hidden bg-neutral-100">
                  {n.cover_image_url ? (
                    <img
                      src={n.cover_image_url.startsWith("http") ? n.cover_image_url : `${API.replace("/api","")}${n.cover_image_url}`}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground/60"
                      style={{ background: `linear-gradient(135deg, hsl(${(idx * 60) % 360},35%,92%) 0%, hsl(${(idx * 60 + 30) % 360},25%,85%) 100%)` }}>
                      <svg width="72" height="72" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" className="opacity-50">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <path d="M21 15l-5-5L5 21" />
                      </svg>
                      <span className="text-xs uppercase tracking-wider mt-2 opacity-70">Cover non impostata</span>
                    </div>
                  )}
                  <div className="absolute top-4 left-4 px-3 py-1 bg-white text-foreground text-xs font-medium rounded-full">
                    {n.display_name}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-heading font-bold text-2xl mb-1">{n.name}</h3>
                  <div className="text-sm text-muted-foreground mb-4">
                    {n.terminology?.casa_madre_label} → {n.terminology?.rivenditori_label_plural}
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <div className="flex gap-5 text-sm">
                      <div>
                        <div className="font-heading font-bold text-xl">{n.stats?.rivenditori || 0}</div>
                        <div className="text-xs text-muted-foreground">{t("rivenditori")}</div>
                      </div>
                      <div>
                        <div className="font-heading font-bold text-xl">{n.stats?.prodotti || 0}</div>
                        <div className="text-xs text-muted-foreground">{t("prodotti")}</div>
                      </div>
                    </div>
                    <span className="text-brand text-sm font-medium inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
                      {t("storefront_explore")} <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="text-sm text-muted-foreground">
            © 2026 TotalKlick SRLS · Andrea Sorrentino · Universal Multi-Network Distribution Hub
          </div>
          <div className="text-xs text-muted-foreground font-mono">
            PRD v3.0 · Emergent-native · {lang.toUpperCase()}
          </div>
        </div>
      </footer>
    </div>
  );
}
